import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus, Trash2, KeyRound } from "lucide-react";

const SPECIALIST_CATEGORIES = ["CA Specialist", "CS Specialist", "Legal Specialist", "Purchase Manager", "Marketing Specialist", "Design Manager", "Software / Technology Specialist", "Website Specialist", "Mobile App Specialist", "Business Consultant", "Finance Specialist", "HR Specialist", "Sales Specialist", "Operations Specialist", "Compliance Specialist", "Other"];

export function SpecialistsModule() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", password: "", specialist_category: "CA Specialist", expertise: "", phone: "" });
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/portal/admin/specialists").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      await api.post("/portal/admin/specialists", form);
      toast.success("Specialist created — they can log in via the portal");
      setForm({ name: "", email: "", password: "", specialist_category: "CA Specialist", expertise: "", phone: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAdding(false);
    }
  };

  const toggleActive = async (s) => {
    try {
      await api.put(`/portal/admin/specialists/${s.id}`, { is_active: !s.is_active });
      toast.success(s.is_active ? "Specialist suspended" : "Specialist activated");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const resetPw = async (s) => {
    const pw = window.prompt(`New password for ${s.email} (min 8 characters):`);
    if (!pw) return;
    try {
      await api.put(`/portal/admin/specialists/${s.id}`, { password: pw });
      toast.success("Password reset");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete specialist "${s.name}"? Assigned orders keep their history.`)) return;
    try {
      await api.delete(`/portal/admin/specialists/${s.id}`);
      toast.success("Specialist deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div data-testid="admin-specialists-module" className="space-y-8">
      <div>
        <h2 className="font-display text-2xl font-bold text-white">Specialists</h2>
        <p className="text-sm text-slate-400">Create and manage specialist accounts and their workload.</p>
      </div>

      <form onSubmit={add} className="glass rounded-xl p-6 space-y-4" data-testid="specialist-add-form">
        <p className="font-mono-tech text-[10px] tracking-[0.3em] text-[#FF8800] uppercase flex items-center gap-2"><UserPlus className="w-3.5 h-3.5" /> Add Specialist</p>
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label className="text-slate-300">Name *</Label>
            <Input data-testid="specialist-field-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-[#06080E] border-white/10" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Email *</Label>
            <Input data-testid="specialist-field-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-[#06080E] border-white/10" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Password * (min 8)</Label>
            <Input data-testid="specialist-field-password" type="password" required minLength={8} value={form.password} onChange={(e) => set("password", e.target.value)} className="bg-[#06080E] border-white/10" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Category</Label>
            <Select value={form.specialist_category} onValueChange={(v) => set("specialist_category", v)}>
              <SelectTrigger data-testid="specialist-field-category" className="bg-[#06080E] border-white/10 text-slate-200"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0D111D] border-white/10 text-slate-200 max-h-56">
                {SPECIALIST_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label className="text-slate-300">Expertise</Label>
            <Input data-testid="specialist-field-expertise" value={form.expertise} onChange={(e) => set("expertise", e.target.value)} placeholder="e.g. GST, ROC filings, FSSAI" className="bg-[#06080E] border-white/10" />
          </div>
        </div>
        <Button type="submit" disabled={adding} data-testid="specialist-add-submit" className="bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold">
          {adding ? "Creating..." : "Create Specialist"}
        </Button>
      </form>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 rounded-full border-2 border-[#FF8800] border-t-transparent animate-spin" /></div>
      ) : (
        <div className="space-y-3" data-testid="specialists-list">
          {items.map((s) => (
            <div key={s.id} className="glass rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`specialist-row-${s.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-display font-semibold text-white">{s.name}</p>
                  <span className="text-[10px] font-mono-tech px-2 py-0.5 rounded-full border border-[#00B0FF]/40 text-[#00B0FF]">{s.specialist_category}</span>
                  <span className={`text-[10px] font-mono-tech ${s.is_active ? "text-[#00E676]" : "text-red-400"}`}>{s.is_active ? "ACTIVE" : "SUSPENDED"}</span>
                </div>
                <p className="text-xs text-slate-400 mt-1">{s.email} · {s.expertise || "—"} · {s.active_orders} active · {s.completed_orders} completed</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Switch checked={s.is_active} onCheckedChange={() => toggleActive(s)} data-testid={`specialist-toggle-${s.id}`} />
                <Button size="icon" variant="outline" data-testid={`specialist-reset-${s.id}`} onClick={() => resetPw(s)} className="border-white/15 text-slate-300 hover:bg-white/5" title="Reset password"><KeyRound className="w-4 h-4" /></Button>
                <Button size="icon" variant="outline" data-testid={`specialist-delete-${s.id}`} onClick={() => remove(s)} className="border-red-500/30 text-red-400 hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
